import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  OutlinedInput,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ICustomPushApiKey, ICustomPushApiKeyCreateResponse } from '../../types/customPush';
import { customPushService } from '../../services/customPushService';

// <input type="datetime-local"> works in local time with no timezone info,
// while the API sends/accepts ISO 8601 (UTC) strings - these convert
// between the two without going through UTC-vs-local off-by-hours bugs.
const isoToDatetimeLocal = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

const datetimeLocalToIso = (value: string): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

interface ApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
  apiKey: ICustomPushApiKey | null;
  notificationTypeOptions: string[];
  onSaved: () => void;
  onCreated: (result: ICustomPushApiKeyCreateResponse) => void;
}

const ApiKeyDialog: React.FC<ApiKeyDialogProps> = ({
  open,
  onClose,
  apiKey,
  notificationTypeOptions,
  onSaved,
  onCreated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = !!apiKey;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notificationTypes, setNotificationTypes] = useState<string[]>([]);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setName(apiKey?.name || '');
      setDescription(apiKey?.description || '');
      setNotificationTypes(apiKey?.notificationTypes || []);
      setValidFrom(isoToDatetimeLocal(apiKey?.validFrom));
      setValidUntil(isoToDatetimeLocal(apiKey?.validUntil));
      setErrors({});
    }
  }, [open, apiKey]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (notificationTypes.length === 0) newErrors.notificationTypes = 'Select at least one notification type';
    if (validFrom && validUntil && new Date(validUntil) <= new Date(validFrom)) {
      newErrors.validUntil = 'Valid until must be after valid from';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const validFromIso = datetimeLocalToIso(validFrom);
      const validUntilIso = datetimeLocalToIso(validUntil);
      if (isEdit && apiKey?.apiKeyId) {
        await customPushService.updateApiKey(
          apiKey.apiKeyId,
          name.trim(),
          description.trim(),
          notificationTypes,
          validFromIso,
          validUntilIso
        );
        onSaved();
        onClose();
      } else {
        const result = await customPushService.createApiKey(
          name.trim(),
          description.trim(),
          notificationTypes,
          validFromIso,
          validUntilIso
        );
        onSaved();
        onClose();
        onCreated(result);
      }
    } catch (error: any) {
      setErrors({ name: typeof error === 'string' ? error : 'Failed to save API key' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit API Key' : 'New API Key'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FormControl fullWidth error={!!errors.notificationTypes} required>
            <InputLabel id="api-key-notification-types-label">Notification Types</InputLabel>
            <Select
              labelId="api-key-notification-types-label"
              multiple
              value={notificationTypes}
              onChange={(e) => setNotificationTypes(e.target.value as string[])}
              input={<OutlinedInput label="Notification Types" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {notificationTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Valid From"
              type="datetime-local"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Optional. Leave blank for no start restriction."
            />
            <TextField
              fullWidth
              label="Valid Until"
              type="datetime-local"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!errors.validUntil}
              helperText={errors.validUntil || 'Optional. Leave blank for no expiry.'}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} fullWidth={isMobile}>
          Close
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading} fullWidth={isMobile}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyDialog;
